import { API } from "config";
import type { Memory } from "types/Memory";
import request from "utilities/request";

// Already ends in `/chat`.
const baseUrl = API.dbApi.url;

interface GetMemoriesResponseBody {
  memories: Memory[];
}

async function authHeaders(getAccessToken: () => Promise<string>) {
  const accessToken = await getAccessToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
}

/**
 * Returns the user's memories about themselves and, with `threadId`, that
 * chat's pinned memories too, in one list.
 */
export async function getMemories(
  threadId: string | undefined,
  getAccessToken: () => Promise<string>
): Promise<Memory[]> {
  const query = threadId ? `?threadId=${threadId}` : "";
  const url = `${baseUrl}/memories${query}`;
  const options = { method: "GET", headers: await authHeaders(getAccessToken) };

  const response = await request(url, options);
  return ((await response.json()) as GetMemoriesResponseBody).memories;
}

export async function postMemory(
  input: { content: string; threadId?: string },
  getAccessToken: () => Promise<string>
): Promise<Memory> {
  const url = `${baseUrl}/memories`;
  const options = {
    method: "POST",
    headers: await authHeaders(getAccessToken),
    body: JSON.stringify(input),
  };

  const response = await request(url, options);
  return (await response.json()) as Memory;
}

export async function patchMemory(
  memoryId: string,
  content: string,
  getAccessToken: () => Promise<string>
): Promise<Memory> {
  const url = `${baseUrl}/memories/${memoryId}`;
  const options = {
    method: "PATCH",
    headers: await authHeaders(getAccessToken),
    body: JSON.stringify({ content }),
  };

  const response = await request(url, options);
  return (await response.json()) as Memory;
}

/** Resolves on 204. Deleting a memory that no longer exists also succeeds. */
export async function deleteMemory(
  memoryId: string,
  getAccessToken: () => Promise<string>
): Promise<void> {
  const url = `${baseUrl}/memories/${memoryId}`;
  const options = {
    method: "DELETE",
    headers: await authHeaders(getAccessToken),
  };

  await request(url, options);
}

/** Deletes every memory about the user. Memories pinned to chats stay. */
export async function deleteUserMemories(
  getAccessToken: () => Promise<string>
): Promise<void> {
  const url = `${baseUrl}/memories`;
  const options = {
    method: "DELETE",
    headers: await authHeaders(getAccessToken),
  };

  await request(url, options);
}
